'use client';

/**
 * The voice first.
 *
 * A parent who came for "read it in my voice" was asked for a plot, a hero, a
 * genre and a length before anyone mentioned a voice — it sat inside the output
 * screen, three steps in, next to the book settings (gap 1). On this road the
 * voice is the first screen, because it is the reason for the order.
 *
 * Three ways to have one, in the order that costs the family least:
 *   1. a voice already in the library — on the second visit this is the whole
 *      screen, and nothing is recorded again;
 *   2. record mine — the one-take wizard;
 *   3. ask somebody else (grandma, grandad) — the link they open with no
 *      account, read our passage, and their voice lands in this library.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { Mic, Users } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { VoiceCloneWizard } from '@/contexts/ai';
import { VoiceInvitePanel } from '@/contexts/ai/components/VoiceInvitePanel';
import type { CustomVoiceEntry } from '@/contexts/ai/types/voice';
import { useCurrentUserContext } from '@/contexts/authentication/client';
import { useEventSourceEvent } from '@/shared/hooks/useEventSourceEvent';
import { storyKeys, VoiceSelector, type Language } from '@/contexts/stories';
import { useCustomVoices } from '@/contexts/stories/hooks';
import type { VoiceSettings } from './VoiceStep';

interface VoiceChoiceStepProps {
  locale: string;
  language?: string;
  voiceSettings: VoiceSettings;
  onVoiceSettingsChange: (v: VoiceSettings) => void;
  /** The family already has cloned voices → open on choosing, not on recording. */
  hasOwnVoices: boolean;
  /** Select the newest cloned voice instead of a preset (the `voice` road). */
  preferOwnVoice?: boolean;
}

export function VoiceChoiceStep({
  locale,
  language,
  voiceSettings,
  onVoiceSettingsChange,
  hasOwnVoices,
  preferOwnVoice,
}: VoiceChoiceStepProps) {
  const t = useTranslations('content.wizard.voiceChoice');
  const queryClient = useQueryClient();
  const userCtx = useCurrentUserContext();
  const familyId = userCtx?.user?.familyId ?? null;
  const [showClone, setShowClone] = useState(false);
  const [showInvite, setShowInvite] = useState(!hasOwnVoices);
  const pendingVoiceEntryIdRef = useRef<string | null>(null);

  // The second visit does not re-ask: a family that already cloned a voice gets
  // it selected, because that voice IS why they came back.
  const ownVoices = useCustomVoices(preferOwnVoice ? familyId : null);
  const preselected = useRef(false);
  useEffect(() => {
    if (!preferOwnVoice || preselected.current) return;
    const ready = (ownVoices.data ?? []).find((v: { voiceId?: string }) => !!v.voiceId);
    if (!ready) return;
    preselected.current = true;
    onVoiceSettingsChange({
      ...voiceSettings,
      voiceId: ready.voiceId as string,
      voiceName: (ready as { name?: string }).name ?? t('yourVoice'),
    });
    // one-shot, on the first list that arrives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferOwnVoice, ownVoices.data]);

  // A clone finished while the parent is still on this screen → select it.
  useEventSourceEvent('voice.clone.completed', (payload: Record<string, unknown>) => {
    const voiceEntryId = payload.voiceEntryId as string;
    if (voiceEntryId !== pendingVoiceEntryIdRef.current) return;
    pendingVoiceEntryIdRef.current = null;
    const ready: CustomVoiceEntry = {
      id: voiceEntryId,
      voiceId: (payload.voiceId as string) || voiceEntryId,
      name: (payload.name as string) || t('yourVoice'),
      status: 'ready',
    } as CustomVoiceEntry;
    if (familyId) {
      queryClient.setQueryData(storyKeys.customVoices(familyId), (old: unknown[] | undefined) => [
        ready, ...(old ?? []),
      ]);
      queryClient.invalidateQueries({ queryKey: storyKeys.customVoices(familyId) });
    }
    onVoiceSettingsChange({ ...voiceSettings, voiceId: ready.voiceId, voiceName: ready.name });
  });

  return (
    <div className="space-y-5" data-testid="voice-choice-step">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">{t('title')}</h2>
        <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
      </div>

      <VoiceSelector
        selectedVoiceId={voiceSettings.voiceId}
        onVoiceSelect={(voiceId, voiceName) =>
          onVoiceSettingsChange({ ...voiceSettings, voiceId, voiceName })
        }
        familyId={familyId ?? undefined}
        language={language as Language | undefined}
      />

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => setShowClone(true)}
          data-testid="voice-choice-record"
        >
          <Mic className="size-4" />
          {t('recordMine')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => setShowInvite((v) => !v)}
          data-testid="voice-choice-ask"
        >
          <Users className="size-4" />
          {t('askSomeone')}
        </Button>
      </div>

      {showInvite && (
        <div className="rounded-md border bg-muted/30 p-3">
          <VoiceInvitePanel locale={locale} />
        </div>
      )}

      {familyId && (
        <VoiceCloneWizard
          open={showClone}
          onOpenChange={setShowClone}
          familyId={familyId}
          onEnqueued={(voiceEntryId) => { pendingVoiceEntryIdRef.current = voiceEntryId; }}
        />
      )}
    </div>
  );
}
